import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import { IUser } from '../../UserSlice';
import { ISubscriptionPlan } from '../SubscriptionPlansSlice';
import { getPaymentHistories } from './api';
import {
  adminCreatePaymentHistoryAdminNote,
  adminDeletePaymentHistoryAdminNote,
  adminGetPaymentHistories,
} from './adminApi';
import { IInvoice } from '../InvoicesSlice';
import { ICourse, TCoursePricingType } from '../CoursesSlice';
import { IFlexiblePlan } from '../FlexiblePlansSlice';

export const PAYMENT_HISTORY_SUBSCRIPTION_TYPES = {
  FLEXIBLE: 'FLEXIBLE',
  SUBSCRIPTION: 'SUBSCRIPTION',
  INVOICE: 'INVOICE',
  CUSTOM_PLAN: 'CUSTOM_PLAN',
  COURSE: 'COURSE',
  PREMIUM_UPGRADE: 'PREMIUM_UPGRADE',
  COURSE_TOPUP: 'COURSE_TOPUP',
  BUNDLE_COURSE_TOPUP: 'BUNDLE_COURSE_TOPUP',
  AUTO_COURSE_TOPUP: 'AUTO_COURSE_TOPUP',
  INVOICE_MONTHLY_SUBSCRIPTION: 'INVOICE_MONTHLY_SUBSCRIPTION',
  INVOICE_MONTHLY_SUBSCRIPTION_RENEWAL: 'INVOICE_MONTHLY_SUBSCRIPTION_RENEWAL',
  COURSE_MONTHLY_SUBSCRIPTION_PLAN_CHANGE:
    'COURSE_MONTHLY_SUBSCRIPTION_PLAN_CHANGE',
} as const;

export const PAYMENT_HISTORY_METHODS = {
  STRIPE: 'STRIPE',
} as const;

export type TPaymentHistorySubscriptionType = typeof PAYMENT_HISTORY_SUBSCRIPTION_TYPES[keyof typeof PAYMENT_HISTORY_SUBSCRIPTION_TYPES];
export type TPaymentHistoryMethod = typeof PAYMENT_HISTORY_METHODS[keyof typeof PAYMENT_HISTORY_METHODS];

export interface IPaymentHistoryAdminNote {
  _id: string;
  description: string;
  createdBy: string | IUser;
  createdAt: string;
}

export interface IPaymentHistory {
  _id: string;
  reference: string;
  user: string | IUser;
  amount: number;
  currency: string;
  transactionDate: string;
  planMinutes: number;
  subscriptionType: TPaymentHistorySubscriptionType;
  method: TPaymentHistoryMethod;
  flexiblePlan?: string | IFlexiblePlan;
  subscriptionPlan?: string | ISubscriptionPlan;
  invoice?: string | IInvoice;
  isCustomPlan?: boolean;
  isPremiumUpgrade?: boolean;
  adminNotes?: IPaymentHistoryAdminNote[];
  updatedBy?: string | IUser;
  coursePricingType?: TCoursePricingType;
  course?: string | ICourse;
}

export interface IInitialPaymentHistoryState {
  status: 'idle' | 'loading' | 'rejected';
  paymentHistoryListData?: {
    imit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IPaymentHistory>;
  };
  // ADMIN
  adminPaymentHistoryListData?: {
    imit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IPaymentHistory>;
  };
}

const initialState: IInitialPaymentHistoryState = {
  status: 'idle',
  paymentHistoryListData: undefined,
  // ADMIN
  adminPaymentHistoryListData: undefined,
};

export const PaymentHistorySlice = createSlice({
  initialState,
  name: 'paymentHistory',
  reducers: {
    resetPaymentHistoryListData: (state) => {
      state.paymentHistoryListData = undefined;
    },

    // ADMIN
    resetadminPaymentHistoryListData: (state) => {
      state.adminPaymentHistoryListData = undefined;
    },
  },
  extraReducers(builder) {
    builder.addCase(getPaymentHistories.fulfilled, (state, action) => {
      const result = action.payload;

      const newPaymentHistoryListData = {
        limit: result.limit,
        offset: result.offset,
        totalDocs: result.totalDocs || 0,
        list: result.docs || [],
      };

      state.paymentHistoryListData = newPaymentHistoryListData;
    });

    // Admin
    builder
      .addCase(adminGetPaymentHistories.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminPaymentHistoryListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminPaymentHistoryListData = newAdminPaymentHistoryListData;
      })
      .addCase(
        adminCreatePaymentHistoryAdminNote.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminPaymentHistoryListData) {
            const newAdminPaymentHistoryListData = {
              ...state.adminPaymentHistoryListData,
            };

            const index = newAdminPaymentHistoryListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminPaymentHistoryListData.list[index] = result;
            }

            state.adminPaymentHistoryListData = newAdminPaymentHistoryListData;
          }
        }
      )
      .addCase(
        adminDeletePaymentHistoryAdminNote.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (state.adminPaymentHistoryListData) {
            const newAdminPaymentHistoryListData = {
              ...state.adminPaymentHistoryListData,
            };

            const index = newAdminPaymentHistoryListData.list.findIndex(
              (item) => item._id === result._id
            );

            if (index > -1) {
              newAdminPaymentHistoryListData.list[index] = result;
            }

            state.adminPaymentHistoryListData = newAdminPaymentHistoryListData;
          }
        }
      );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        getPaymentHistories,
        // ADMIN
        adminGetPaymentHistories,
        adminCreatePaymentHistoryAdminNote,
        adminDeletePaymentHistoryAdminNote
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        getPaymentHistories,
        // ADMIN
        adminGetPaymentHistories,
        adminCreatePaymentHistoryAdminNote,
        adminDeletePaymentHistoryAdminNote
      ),
      (state) => {
        state.status = 'rejected';
      }
    );

    builder.addMatcher(
      isPending(
        getPaymentHistories,
        // ADMIN
        adminGetPaymentHistories,
        adminCreatePaymentHistoryAdminNote,
        adminDeletePaymentHistoryAdminNote
      ),
      (state) => {
        state.status = 'loading';
      }
    );
  },
});

export const getPaymentHistoryState = (
  state: RootState
): RootState['paymentHistory'] => state.paymentHistory;

export const {
  resetPaymentHistoryListData,
  // ADMIN
  resetadminPaymentHistoryListData,
} = PaymentHistorySlice.actions;

export * from './api';
export * from './adminApi';
export default PaymentHistorySlice.reducer;
